import React, {useEffect, useMemo, useState} from "react";
import {Link as RouterLink, useParams} from "react-router-dom";
import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BarChartIcon from "@mui/icons-material/BarChart";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import {useTranslation} from "react-i18next";

import BreadcrumbLink from "../components/BreadcrumbLink";
import {useAlert} from "../context/AlertContext";
import {getTeacherUserAnalytics} from "../api/api";
import {AdminUserAnalyticsResponse} from "../types";

const fmtPct = (v: number) => `${Math.round(v * 1000) / 10}%`;

const fmtDate = (iso: string | null | undefined, locale?: string) => {
    if (!iso) return "—";

    const d = new Date(iso);

    return isNaN(d.getTime()) ? iso : d.toLocaleString(locale);
};

const getInitials = (fullName?: string, email?: string) => {
    const value = fullName?.trim() || email?.trim();

    if (!value) return "?";

    const parts = value.split(/\s+/);

    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const StatusChip = ({status, label}: { status: string; label: string }) => {
    const ok = status === "ACCEPTED";
    const failed = status === "FAILED";

    return (
        <Chip
            size="small"
            icon={ok ? <CheckCircleIcon sx={{fontSize: 16}}/> : <CancelIcon sx={{fontSize: 16}}/>}
            label={label}
            color={ok ? "success" : failed ? "error" : "default"}
            variant={ok || failed ? "filled" : "outlined"}
            sx={{
                fontWeight: 700,
                height: 22,
                fontSize: 12,
                "& .MuiChip-label": {px: 0.75},
                "& .MuiChip-icon": {ml: 0.5, mr: -0.25},
            }}
        />
    );
};

const StatCard = ({
                      title,
                      value,
                      hint,
                  }: {
    title: string;
    value: React.ReactNode;
    hint?: string;
}) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
                theme.palette.mode === "dark"
                    ? "0 14px 32px rgba(0,0,0,0.22)"
                    : "0 14px 32px rgba(15,23,42,0.06)",
        })}
    >
        <Box
            sx={(theme) => ({
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${theme.palette.action.hover}`,
            })}
        >
            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 700}}>
                {title}
            </Typography>

            <Typography sx={{fontSize: {xs: 24, md: 28}, fontWeight: 900, mt: 0.5}}>
                {value}
            </Typography>

            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    </Paper>
);

const Section = ({
                     title,
                     right,
                     children,
                 }: {
    title: string;
    right?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
        })}
    >
        <Box
            sx={(theme) => ({
                px: {xs: 2, md: 3},
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderBottom: `1px solid ${theme.palette.action.hover}`,
                backgroundColor: theme.palette.background.paper,
            })}
        >
            <BarChartIcon fontSize="small"/>
            <Typography sx={{fontWeight: 900}}>{title}</Typography>

            <Box sx={{flexGrow: 1}}/>
            {right ? right : null}
        </Box>

        <Box sx={{p: {xs: 1.5, md: 3}}}>{children}</Box>
    </Paper>
);

const MobileMetricCard = ({
                              label,
                              value,
                              hint,
                          }: {
    label: string;
    value: React.ReactNode;
    hint?: string;
}) => (
    <Box
        sx={(theme) => ({
            p: 1.25,
            borderRadius: 2.5,
            backgroundColor: theme.palette.action.hover,
        })}
    >
        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 800}}>
            {label}
        </Typography>

        <Typography sx={{fontWeight: 900, mt: 0.25}}>
            {value}
        </Typography>

        {hint ? (
            <Typography variant="caption" color="text.secondary">
                {hint}
            </Typography>
        ) : null}
    </Box>
);

const MobileInfoRow = ({
                           label,
                           value,
                       }: {
    label: string;
    value: React.ReactNode;
}) => (
    <Box sx={{display: "flex", justifyContent: "space-between", gap: 1.5}}>
        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 800}}>
            {label}
        </Typography>

        <Typography variant="body2" sx={{fontWeight: 800, textAlign: "right"}}>
            {value}
        </Typography>
    </Box>
);

const tableCellSx = {
    borderBottom: "none",
    py: 1.5,
};

const TeacherUserAnalyticsPage = () => {
    const {t, i18n} = useTranslation();

    const {userId} = useParams<{ userId?: string }>();
    const safeUserId = Number(userId || 0);

    const {showAlert} = useAlert();

    const [data, setData] = useState<AdminUserAnalyticsResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const getStatusLabel = (status: string) => {
        if (status === "ACCEPTED") return t("streamuseranalytics.statusLabels.accepted");
        if (status === "FAILED") return t("streamuseranalytics.statusLabels.failed");

        return status;
    };

    const fetchData = async () => {
        if (!safeUserId) return;

        try {
            setLoading(true);

            const res = await getTeacherUserAnalytics(safeUserId);

            setData(res);
        } catch (e: any) {
            showAlert(e?.message ?? t("streamuseranalytics.errors.loadAnalytics"), "error");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeUserId]);

    const userTitle = data?.user?.fullName || data?.user?.email || (loading ? t("common.loading") : t("streamuseranalytics.user"));

    const statusMap = useMemo(() => {
        const m = new Map<string, number>();

        (data?.byStatus ?? []).forEach((s) => m.set(s.status, s.count));

        return m;
    }, [data]);

    const successRatePercent = data ? Math.round(data.summary.successRate * 100) : 0;

    return (
        <Container
            maxWidth="xl"
            sx={{
                px: {xs: 1.5, sm: 2, md: 3},
            }}
        >
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small"/>} sx={{mb: 2}}>
                <BreadcrumbLink to="/my-streams">
                    {t("breadcrumb.myStreams")}
                </BreadcrumbLink>

                <BreadcrumbLink to={-1 as any}>
                    {t("streamuseranalytics.users")}
                </BreadcrumbLink>

                <Typography color="text.primary" noWrap variant="body2" sx={{fontWeight: 600}}>
                    {userTitle}
                </Typography>
            </Breadcrumbs>

            <Paper
                elevation={0}
                sx={(theme) => ({
                    borderRadius: 3,
                    overflow: "hidden",
                    mb: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                })}
            >
                <Box
                    sx={(theme) => ({
                        px: {xs: 1.5, md: 3},
                        py: 2,
                        display: "flex",
                        alignItems: {xs: "flex-start", sm: "center"},
                        flexDirection: {xs: "column", sm: "row"},
                        gap: 1.5,
                        borderBottom: `1px solid ${theme.palette.action.hover}`,
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5, minWidth: 0}}>
                        <Avatar
                            sx={(theme) => ({
                                width: 46,
                                height: 46,
                                fontSize: 15,
                                fontWeight: 900,
                                bgcolor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                flexShrink: 0,
                            })}
                        >
                            {data?.user ? getInitials(data.user.fullName, data.user.email) : <PersonIcon/>}
                        </Avatar>

                        <Box sx={{minWidth: 0}}>
                            <Typography sx={{fontWeight: 900}} noWrap>
                                {userTitle}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {t("streamuseranalytics.id")}: {data?.user?.id ?? safeUserId}
                                {"  •  "}
                                {data?.user?.email ?? "—"}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                    <Button
                        component="button"
                        onClick={fetchData}
                        variant="outlined"
                        size="small"
                        sx={{
                            textTransform: "none",
                            width: {xs: "100%", sm: "auto"},
                            fontWeight: 800,
                        }}
                    >
                        {t("streamuseranalytics.refresh")}
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                        <CircularProgress/>
                    </Box>
                ) : !data ? (
                    <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                        <Typography variant="h6" sx={{mb: 1}}>
                            {t("common.noData")}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {t("streamuseranalytics.analyticsUnavailable")}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{p: {xs: 1.5, md: 3}}}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr 1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)"},
                                gap: {xs: 1.25, md: 2},
                            }}
                        >
                            <StatCard title={t("streamuseranalytics.totalAttempts")} value={data.summary.totalAttempts}/>
                            <StatCard title={t("streamuseranalytics.acceptedAttempts")} value={data.summary.acceptedAttempts}/>
                            <StatCard title={t("streamuseranalytics.uniqueSolvedTasks")} value={data.summary.uniqueSolvedTasks}/>
                            <StatCard title={t("streamuseranalytics.successRate")} value={fmtPct(data.summary.successRate)}/>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                mt: 1.5,
                                p: 1.5,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                background:
                                    theme.palette.mode === "dark"
                                        ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                        : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                            })}
                        >
                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                <Box>
                                    <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                        {t("streamuseranalytics.progressOverview")}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        {t("streamuseranalytics.progressOverviewDescription")}
                                    </Typography>
                                </Box>

                                <Chip
                                    size="small"
                                    label={fmtPct(data.summary.successRate)}
                                    sx={(theme) => ({
                                        fontWeight: 900,
                                        backgroundColor: theme.palette.action.hover,
                                    })}
                                />
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.max(0, successRatePercent))}
                                sx={{
                                    mt: 1.5,
                                    height: 8,
                                    borderRadius: 999,
                                }}
                            />

                            <Box
                                sx={{
                                    mt: 1.5,
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Chip
                                    label={`${t("streamuseranalytics.firstAttempt")}: ${fmtDate(data.summary.firstAttemptAt, i18n.language)}`}
                                    variant="outlined"
                                    size="small"
                                />

                                <Chip
                                    label={`${t("streamuseranalytics.lastAttempt")}: ${fmtDate(data.summary.lastAttemptAt, i18n.language)}`}
                                    variant="outlined"
                                    size="small"
                                />

                                <Chip
                                    label={`${t("streamuseranalytics.accepted")}: ${statusMap.get("ACCEPTED") ?? 0}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />

                                <Chip
                                    label={`${t("streamuseranalytics.failed")}: ${statusMap.get("FAILED") ?? 0}`}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>

            {data ? (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: 2, mb: 2}}>
                    <Section title={t("streamuseranalytics.byStatus")}>
                        <Box sx={{display: {xs: "none", md: "block"}}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{background: "#fafafa"}}>
                                        <TableCell sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.status")}
                                        </TableCell>

                                        <TableCell align="right" sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.count")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {data.byStatus.map((s) => (
                                        <TableRow key={s.status} hover>
                                            <TableCell sx={tableCellSx}>
                                                <StatusChip status={s.status} label={getStatusLabel(s.status)}/>
                                            </TableCell>

                                            <TableCell align="right" sx={tableCellSx}>
                                                {s.count}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Box sx={{display: {xs: "flex", md: "none"}, flexDirection: "column", gap: 1}}>
                            {data.byStatus.map((s) => (
                                <Box
                                    key={s.status}
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    })}
                                >
                                    <StatusChip status={s.status} label={getStatusLabel(s.status)}/>

                                    <Typography sx={{fontWeight: 900, fontSize: 20}}>
                                        {s.count}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Section>

                    <Section title={t("streamuseranalytics.byLanguage")}>
                        <Box sx={{display: {xs: "none", md: "block"}}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{background: "#fafafa"}}>
                                        <TableCell sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.language")}
                                        </TableCell>

                                        <TableCell align="right" sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.attempts")}
                                        </TableCell>

                                        <TableCell align="right" sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.accepted")}
                                        </TableCell>

                                        <TableCell align="right" sx={{fontWeight: 600}}>
                                            {t("streamuseranalytics.uniqueSolved")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {data.byLanguage.map((r) => (
                                        <TableRow key={r.language} hover>
                                            <TableCell sx={tableCellSx}>{r.language}</TableCell>
                                            <TableCell align="right" sx={tableCellSx}>{r.attempts}</TableCell>
                                            <TableCell align="right" sx={tableCellSx}>{r.accepted}</TableCell>
                                            <TableCell align="right" sx={tableCellSx}>{r.uniqueSolved}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Box sx={{display: {xs: "flex", md: "none"}, flexDirection: "column", gap: 1}}>
                            {data.byLanguage.map((r) => (
                                <Box
                                    key={r.language}
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        boxShadow:
                                            theme.palette.mode === "dark"
                                                ? "0 12px 28px rgba(0,0,0,0.26)"
                                                : "0 12px 28px rgba(15,23,42,0.06)",
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <CodeIcon sx={{fontSize: 20, color: "text.secondary"}}/>

                                        <Typography sx={{fontWeight: 900}}>
                                            {r.language}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            mt: 1.25,
                                            display: "grid",
                                            gridTemplateColumns: "repeat(3, 1fr)",
                                            gap: 1,
                                        }}
                                    >
                                        <MobileMetricCard label={t("streamuseranalytics.attempts")} value={r.attempts}/>
                                        <MobileMetricCard label={t("streamuseranalytics.accepted")} value={r.accepted}/>
                                        <MobileMetricCard label={t("streamuseranalytics.solved")} value={r.uniqueSolved}/>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Section>
                </Box>
            ) : null}

            {data ? (
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <Section title={t("streamuseranalytics.byCourse")}>
                        {data.byCourse.length === 0 ? (
                            <Typography color="text.secondary">
                                {t("streamuseranalytics.noCourseStats")}
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{display: {xs: "none", md: "block"}}}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{background: "#fafafa"}}>
                                                <TableCell sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.course")}
                                                </TableCell>

                                                <TableCell align="right" sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.attempts")}
                                                </TableCell>

                                                <TableCell align="right" sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.uniqueSolved")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {data.byCourse.map((c) => (
                                                <TableRow key={c.courseId} hover>
                                                    <TableCell sx={tableCellSx}>{c.courseTitle}</TableCell>
                                                    <TableCell align="right" sx={tableCellSx}>{c.attempts}</TableCell>
                                                    <TableCell align="right" sx={tableCellSx}>{c.uniqueSolved}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>

                                <Box sx={{display: {xs: "flex", md: "none"}, flexDirection: "column", gap: 1}}>
                                    {data.byCourse.map((c) => (
                                        <Box
                                            key={c.courseId}
                                            sx={(theme) => ({
                                                p: 1.5,
                                                borderRadius: 3,
                                                border: `1px solid ${theme.palette.divider}`,
                                                backgroundColor: theme.palette.background.paper,
                                                boxShadow:
                                                    theme.palette.mode === "dark"
                                                        ? "0 12px 28px rgba(0,0,0,0.26)"
                                                        : "0 12px 28px rgba(15,23,42,0.06)",
                                            })}
                                        >
                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <SchoolIcon sx={{fontSize: 20, color: "text.secondary", mt: 0.1}}/>

                                                <Box sx={{minWidth: 0}}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 900,
                                                            lineHeight: 1.35,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {c.courseTitle}
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        {t("streamuseranalytics.courseId")}: {c.courseId}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    mt: 1.25,
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 1fr",
                                                    gap: 1,
                                                }}
                                            >
                                                <MobileMetricCard label={t("streamuseranalytics.attempts")} value={c.attempts}/>
                                                <MobileMetricCard label={t("streamuseranalytics.uniqueSolved")} value={c.uniqueSolved}/>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Section>

                    <Section
                        title={t("streamuseranalytics.recentAttempts")}
                        right={
                            <Box
                                component={RouterLink}
                                to={`/teacher/users/${safeUserId}/attempts`}
                                sx={(theme) => ({
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: theme.palette.text.secondary,
                                    textDecoration: "none",
                                    whiteSpace: "nowrap",
                                    "&:hover": {color: theme.palette.text.primary},
                                })}
                            >
                                {t("streamuseranalytics.viewAll")}
                            </Box>
                        }
                    >
                        {data.recentAttempts.length === 0 ? (
                            <Typography color="text.secondary">
                                {t("streamuseranalytics.noAttempts")}
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{display: {xs: "none", md: "block"}}}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{background: "#fafafa"}}>
                                                <TableCell sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.task")}
                                                </TableCell>

                                                <TableCell sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.lang")}
                                                </TableCell>

                                                <TableCell sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.status")}
                                                </TableCell>

                                                <TableCell align="right" sx={{fontWeight: 600}}>
                                                    {t("streamuseranalytics.time")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {data.recentAttempts.map((a) => (
                                                <TableRow key={a.attemptId} hover>
                                                    <TableCell sx={tableCellSx}>
                                                        <Box
                                                            component={RouterLink}
                                                            to={`/task-details/${a.taskId}`}
                                                            sx={(theme) => ({
                                                                color: "inherit",
                                                                textDecoration: "none",
                                                                display: "inline-block",
                                                                borderBottom: `1px dashed ${theme.palette.divider}`,
                                                                "&:hover": {borderBottomColor: theme.palette.text.primary},
                                                            })}
                                                        >
                                                            #{a.taskId} — {a.taskTitle}
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell sx={tableCellSx}>{a.language}</TableCell>

                                                    <TableCell sx={tableCellSx}>
                                                        <StatusChip status={a.status} label={getStatusLabel(a.status)}/>
                                                    </TableCell>

                                                    <TableCell align="right" sx={tableCellSx}>
                                                        {fmtDate(a.createdAt, i18n.language)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>

                                <Box sx={{display: {xs: "flex", md: "none"}, flexDirection: "column", gap: 1}}>
                                    {data.recentAttempts.map((a) => (
                                        <Box
                                            key={a.attemptId}
                                            sx={(theme) => ({
                                                p: 1.5,
                                                borderRadius: 3,
                                                border: `1px solid ${theme.palette.divider}`,
                                                backgroundColor: theme.palette.background.paper,
                                                boxShadow:
                                                    theme.palette.mode === "dark"
                                                        ? "0 12px 28px rgba(0,0,0,0.26)"
                                                        : "0 12px 28px rgba(15,23,42,0.06)",
                                                position: "relative",
                                                overflow: "hidden",
                                                "&:before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 4,
                                                    backgroundColor:
                                                        a.status === "ACCEPTED"
                                                            ? theme.palette.success.main
                                                            : a.status === "FAILED"
                                                                ? theme.palette.error.main
                                                                : theme.palette.primary.main,
                                                },
                                            })}
                                        >
                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <AssignmentTurnedInIcon
                                                    sx={{
                                                        fontSize: 21,
                                                        color: "text.secondary",
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                    <Box
                                                        component={RouterLink}
                                                        to={`/task-details/${a.taskId}`}
                                                        sx={(theme) => ({
                                                            color: "inherit",
                                                            textDecoration: "none",
                                                            display: "inline",
                                                            fontWeight: 900,
                                                            lineHeight: 1.35,
                                                            wordBreak: "break-word",
                                                            borderBottom: `1px dashed ${theme.palette.divider}`,
                                                            "&:hover": {
                                                                borderBottomColor: theme.palette.text.primary,
                                                            },
                                                        })}
                                                    >
                                                        #{a.taskId} — {a.taskTitle}
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            mt: 0.9,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.75,
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        <StatusChip status={a.status} label={getStatusLabel(a.status)}/>

                                                        <Chip
                                                            size="small"
                                                            label={a.language}
                                                            sx={(theme) => ({
                                                                height: 22,
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                backgroundColor: theme.palette.action.hover,
                                                            })}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={(theme) => ({
                                                    mt: 1.25,
                                                    p: 1.2,
                                                    borderRadius: 2.5,
                                                    backgroundColor: theme.palette.action.hover,
                                                })}
                                            >
                                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 0.75}}>
                                                    <AccessTimeIcon sx={{fontSize: 18, color: "text.secondary"}}/>

                                                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 900}}>
                                                        {t("streamuseranalytics.attemptTime")}
                                                    </Typography>
                                                </Box>

                                                <MobileInfoRow
                                                    label={t("streamuseranalytics.created")}
                                                    value={fmtDate(a.createdAt, i18n.language)}
                                                />

                                                <MobileInfoRow
                                                    label={t("streamuseranalytics.attemptId")}
                                                    value={a.attemptId}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Section>
                </Box>
            ) : null}
        </Container>
    );
};

export default TeacherUserAnalyticsPage;